import { useSwipeable } from 'react-swipeable';
import { useNavigate } from 'react-router-dom';

const DrinksDinner = ({ language }: { language: string }) => {
  const navigate = useNavigate();

  const handlers = useSwipeable({
    onSwipedLeft: () => navigate('/EntreesDinner'),
    onSwipedRight: () => navigate('/DessertDinner'),
    trackMouse: true,
    delta: 50,
    trackTouch: true
  });

  const hotDrinks = [
    { fr: "Expresso", en: "Espresso", priceFr: "4.25$", priceEn: "$4.25" },
    { fr: "Chocolat chaud", en: "Hot Chocolate", priceFr: "4.75$", priceEn: "$4.75" },
    { fr: "Cappuccino", en: "Cappuccino", priceFr: "5.25$", priceEn: "$5.25" },
    { fr: "Thé noir", en: "Black Tea", priceFr: "4.25$", priceEn: "$4.25" },
    { fr: "Thé rouge", en: "Red or Yellow Tea", priceFr: "4.25$", priceEn: "$4.25" },
    { fr: "Thé maghrébin à la menthe", en: "Maghrebi Mint Tea", priceFr: "4.50$", priceEn: "$4.50" },
    { fr: "Tisane au gingembre et citron", en: "Ginger Lemon Herbal Tea", priceFr: "3.75$", priceEn: "$3.75" },
    { fr: "Tisane à la camomille", en: "Chamomile Herbal Tea", priceFr: "3.75$", priceEn: "$3.75" }
  ];

  const coldDrinks = [
    {
      fr: "Limonade aux fruits rouges (maison)",
      en: "Homemade Berry Lemonade",
      priceFr: "6.75$",
      priceEn: "$6.75",
      descFr: "Limonade maison aux fruits rouges, acidulée et douce.",
      descEn: "Homemade berry lemonade, sweet and tangy."
    },
    {
      fr: "Citronnade maison",
      en: "Homemade Lemon Drink",
      priceFr: "6.50$",
      priceEn: "$6.50",
      descFr: "Boisson maison rafraîchissante.",
      descEn: "Refreshing homemade drink."
    },
    {
      fr: "Thé glacé",
      en: "Iced Tea",
      priceFr: "4.75$",
      priceEn: "$4.75",
      descFr: "Thé glacé maison, léger et désaltérant.",
      descEn: "Homemade iced tea, light and refreshing."
    }
  ];

  const smoothies = [
    {
      fr: "Banane, amande, avocat, lait (Version avec lait végétal disponible)",
      en: "Banana, almond, avocado, milk (Version with plant-based milk available)",
      priceFr: "9.25$",
      priceEn: "$9.25"
    },
    {
      fr: "Citron, pomme, tomate, carotte",
      en: "Lemon, apple, tomato, carrot",
      priceFr: "8.50$",
      priceEn: "$8.50"
    },
    {
      fr: "Épinard, ananas, avocat, banane, noix de Grenoble, lait",
      en: "Spinach, pineapple, avocado, banana, walnuts, milk",
      priceFr: "10$",
      priceEn: "$10"
    }
    ,
    {
      fr: "Banane, lait de coco, ananas, mangue",
      en: "Banana, coconut milk, pineapple, mango",
      priceFr: "8.50$",
      priceEn: "$8.50"
    },
    {
      fr: "Kiwi, concombre, miel, yogourt",
      en: "Kiwi, cucumber, honey, yogurt",
      priceFr: "9.25$",
      priceEn: "$9.25"
    }
  ];

  const renderDrinkSection = (titleFr: string, titleEn: string, drinks: any[]) => (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 max-w-2xl w-full shadow-lg mt-8">
      <h2 className="text-2xl font-bold mb-4 text-green-950 text-center">
        {language === "fr" ? titleFr : titleEn}
      </h2>
      <ul className="space-y-6">
        {drinks.map((item, index) => (
          <li key={index} className="border-b border-gray-200 pb-4 last:border-0">
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <h5 className="font-bold text-lg break-words">
                  {language === "fr" ? item.fr : item.en}
                </h5>
                {item.descFr && item.descEn && (
                  <p className="text-gray-600 text-sm mt-1 break-words">
                    {language === "fr" ? item.descFr : item.descEn}
                  </p>
                )}
              </div>
              <span className="text-green-950 font-medium whitespace-nowrap">
                {language === "fr" ? item.priceFr : item.priceEn}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderSmoothieSection = (titleFr: string, titleEn: string, drinks: any[]) => (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 max-w-2xl w-full shadow-lg mt-8">
      <h2 className="text-2xl font-bold mb-4 text-green-950 text-center">
        {language === "fr" ? titleFr : titleEn}
      </h2>
      <ul className="space-y-6">
        {drinks.map((item, index) => (
          <li key={index} className="border-b border-gray-200 pb-4 last:border-0">
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <h5 className="text-gray-600 text-sm mt-1 break-words leading-relaxed">
                  {language === "fr" ? item.fr : item.en}
                </h5>
                {item.descFr && item.descEn && (
                  <p className="text-gray-600 text-sm mt-1 break-words leading-relaxed">
                    {language === "fr" ? item.descFr : item.descEn}
                  </p>
                )}
              </div>
              <span className="text-green-950 font-medium whitespace-nowrap pt-1">
                {language === "fr" ? item.priceFr : item.priceEn}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div
      className="min-h-screen flex flex-col items-center p-8 bg-cover bg-center bg-fixed pt-[110px]"
      style={{ backgroundImage: "url('/image/Lona2.jpg')" }}
      {...handlers}
    >
      <p className="text-center text-xs text-green-950 mt-2">
        {language === "fr" ? "Glissez pour naviguer" : "Swipe to navigate"}
      </p>

      {renderDrinkSection("Boissons Chaudes", "Hot Drinks", hotDrinks)}
      {renderDrinkSection("Boissons Froides", "Cold Drinks", coldDrinks)}
      {renderSmoothieSection("Nos Smoothies", "Smoothies", smoothies)}
    </div>
  );
};

export default DrinksDinner;
